import { useContext, useEffect } from 'react';
import { useHttp, useNotification } from 'src/common/hooks';
import { User } from 'src/common/types/user';
import { AuthContext } from 'src/contexts';

interface Circle {
  id: string;
  name: string;
}

interface DeleteCoWorkerPopupProps {
  member: User;
  circle: Circle;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteCoWorkerPopup = ({
  member,
  circle,
  onClose,
  onDeleted,
}: DeleteCoWorkerPopupProps) => {
  const notify = useNotification();
  const { accessToken } = useContext(AuthContext);

  const {
    sendRequest: deleteMember,
    isLoading: isDeletingMember,
    error: errorDeletingMember,
  } = useHttp();

  useEffect(() => {
    if (errorDeletingMember) {
      notify(errorDeletingMember, 'error');
    }
  });

  const handleDeleteMember = () => {
    deleteMember(
      {
        url: `circle/${circle.id}/user/${member.id}`,
        method: 'DELETE',
        accessToken,
      },
      handleDeleteResponse,
    );
  };

  const handleDeleteResponse = () => {
    notify(`${member.name} was deleted from the “${circle.name}” circle.`, 'success');
    onDeleted();
    onClose();
  };

  return (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-[#0B1444E5] bg-opacity-90 z-50">
      <div className="w-[600px] relative bg-white rounded-lg p-14 shadow-lg flex flex-col">
        <button className="absolute right-3 top-3" onClick={onClose}>
          <svg
            width="43"
            height="42"
            viewBox="0 0 43 42"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.5 15C0.5 6.71573 7.21573 0 15.5 0H27.5C35.7843 0 42.5 6.71573 42.5 15V27C42.5 35.2843 35.7843 42 27.5 42H15.5C7.21573 42 0.5 35.2843 0.5 27V15Z"
              fill="#E5E5E5"
            />
            <path d="M17.5 17L25.5 25" stroke="black" strokeWidth="2" strokeLinecap="round" />
            <path d="M25.5 17L17.5 25" stroke="black" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <h2 className="text-[30px] font-bold font-[sohne-breit] tracking-wide mb-5 text-center leading-[1] flex flex-wrap justify-center">
          Are you sure you want to <span className="highlight-marker">delete</span>{' '}
          <span className="mx-2 truncate highlight-marker">{member.name ?? 'John Doe'}</span> from
          the <span className="mr-1.5 truncate">“{circle.name}”</span>circle?
        </h2>

        <div className="flex gap-6 mx-auto">
          <button
            onClick={handleDeleteMember}
            disabled={isDeletingMember}
            className="px-8 py-[21px] text-xs text-center font-medium font-[sohne-breit] tracking-wider text-white bg-[#C51C00] rounded-lg focus:outline-none hover:bg-opacity-90 transition-opacity flex"
          >
            Yes, delete{' '}
            <span className="truncate max-w-[200px] ml-2">{member.name ?? 'John Doe'}</span>
          </button>

          <button
            disabled={isDeletingMember}
            onClick={onClose}
            className="text-black underline text-opacity-70"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
